import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search, ShoppingCart, X } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductListItem } from './components';
import type { ProductListItem as ProductListItemType, ProductSelectorProps, ProductSelectorRef } from './types';

const ProductSelector = React.memo(
  forwardRef<ProductSelectorRef, ProductSelectorProps>(
    (
      {
        products,
        onSubmit,
        onCancel,
        initialSelectedProducts = [],
        searchPlaceholder,
        title,
        confirmButtonText,
        cancelButtonText,
        maxQuantity = 999,
        onSearchChange,
        onOpenChange,
        onLoadMore,
        hasMore = false,
        isLoading = false,
        error,
        onRetry,
        allowMultiple = true,
        'data-testid': testId = 'product-selector',
      },
      ref,
    ) => {
      // 🌍 Hook de traducción para namespace UI compartido
      const { t } = useTranslation('ui');

      // 🎯 Traducciones con fallback
      const translations = {
        searchPlaceholder: searchPlaceholder || t('productSelector.searchPlaceholder'),
        title: title || t('productSelector.title'),
        confirmButtonText: confirmButtonText || t('productSelector.confirmButton'),
        cancelButtonText: cancelButtonText || t('productSelector.cancelButton'),
        loadingProducts: t('productSelector.loadingProducts'),
        preparingList: t('productSelector.preparingList'),
        noProductsAvailable: t('productSelector.noProductsAvailable'),
        noProductsMessage: t('productSelector.noProductsMessage'),
        noResultsFound: t('productSelector.noResultsFound'),
        noResultsMessage: t('productSelector.noResultsMessage'),
      };

      const modalRef = useRef<ModalCenterRef>(null);
      const virtualizedListRef = useRef<HTMLDivElement>(null);

      const [internalProducts, setInternalProducts] = useState<ProductListItemType[]>([]);
      const [searchTerm, setSearchTerm] = useState('');
      const [isInitializing, setIsInitializing] = useState(false);
      const [remoteOpen, setRemoteOpen] = useState(false);

      useEffect(() => {
        if (!onSearchChange || !remoteOpen) return;
        setInternalProducts((previous) => {
          const selected = previous.filter((row) => row.isSelected);
          return [
            ...products.map(
              (row) => selected.find((item) => item.id === row.id) ?? { ...row, quantity: 1, isSelected: false },
            ),
            ...selected.filter((row) => !products.some((item) => item.id === row.id)),
          ];
        });
      }, [products, onSearchChange, remoteOpen]);

      const initializeProducts = useCallback(() => {
        setIsInitializing(true);
        const productItems = products.map((product) => {
          const existingSelection = initialSelectedProducts.find((selected) => selected.id === product.id);
          return {
            ...product,
            isSelected: !!existingSelection,
            // Usar nullish coalescing para no forzar 1 cuando quantity es 0 o negativa
            quantity: existingSelection?.quantity ?? 1,
          };
        });
        if (onSearchChange)
          productItems.push(
            ...initialSelectedProducts
              .filter((row) => !products.some((product) => product.id === row.id))
              .map((row) => ({ ...row, isSelected: true })),
          );
        setInternalProducts(productItems);
        setSearchTerm('');

        // Pequeño delay solo para permitir que el DOM se actualice
        setTimeout(() => {
          setIsInitializing(false);
        }, 10);
      }, [products, initialSelectedProducts, onSearchChange]);

      const filteredProducts =
        searchTerm.trim() === ''
          ? internalProducts
          : internalProducts.filter((product) => {
              const search = searchTerm.toLowerCase().trim();
              return product.name.toLowerCase().includes(search) || product.code?.toLowerCase().includes(search);
            });

      const virtualizer = useVirtualizer({
        count: filteredProducts.length,
        getScrollElement: () => virtualizedListRef.current,
        estimateSize: () => 80,
        overscan: 50,
      });

      // Forzar re-medición cuando termina la inicialización
      useEffect(() => {
        if (!isInitializing && filteredProducts.length > 0) {
          virtualizer.measure();
        }
      }, [isInitializing, filteredProducts.length, virtualizer]);

      const getSelectedProducts = useCallback(
        () =>
          internalProducts
            .filter((product) => product.isSelected)
            .map((product) => ({
              id: product.id,
              name: product.name,
              code: product.code,
              image: product.image,
              quantity: product.quantity,
            })),
        [internalProducts],
      );

      const handleToggleSelection = useCallback(
        (productId: string | number) => {
          setInternalProducts((prev) => {
            if (!allowMultiple) {
              return prev.map((product) => ({
                ...product,
                isSelected: product.id === productId ? !product.isSelected : false,
                quantity: product.id === productId && !product.isSelected ? product.quantity : 1,
              }));
            }

            return prev.map((product) =>
              product.id === productId
                ? { ...product, isSelected: !product.isSelected, quantity: product.isSelected ? 1 : product.quantity }
                : product,
            );
          });
        },
        [allowMultiple],
      );

      const handleQuantityChange = useCallback(
        (productId: string | number, quantity: number) => {
          setInternalProducts((prev) =>
            prev.map((product) =>
              product.id === productId
                ? { ...product, quantity: Math.max(1, Math.min(maxQuantity, quantity)) }
                : product,
            ),
          );
        },
        [maxQuantity],
      );

      const handleConfirm = useCallback(() => {
        onSubmit?.(getSelectedProducts());
        modalRef.current?.closeModal();
      }, [onSubmit, getSelectedProducts]);

      const handleCancel = useCallback(() => {
        onCancel?.();
        modalRef.current?.closeModal();
      }, [onCancel]);

      const clearSelection = useCallback(() => {
        setInternalProducts((prev) =>
          prev.map((product) => ({
            ...product,
            isSelected: false,
            quantity: 1,
          })),
        );
      }, []);

      const handleOpen = useCallback(() => {
        setRemoteOpen(true);
        onOpenChange?.(true);
        onSearchChange?.('');
        modalRef.current?.openModal();
        initializeProducts();
      }, [initializeProducts, onOpenChange, onSearchChange]);

      const handleClose = useCallback(() => {
        modalRef.current?.closeModal();
        setIsInitializing(false);
      }, []);

      useImperativeHandle(
        ref,
        () => ({
          open: handleOpen,
          close: handleClose,
          clearSelection,
          getSelectedProducts,
        }),
        [handleOpen, handleClose, clearSelection, getSelectedProducts],
      );

      const hasSelectedProducts = internalProducts.some((p) => p.isSelected);
      const hasResults = filteredProducts.length > 0;
      const isSearching = searchTerm.trim().length > 0;

      // Determinar qué mostrar en la lista
      const showLoading =
        isInitializing ||
        (isLoading && !internalProducts.length) ||
        (internalProducts.length === 0 && products.length > 0);
      const showEmpty = !error && !showLoading && internalProducts.length === 0 && products.length === 0;
      const showNoResults = !error && !showLoading && internalProducts.length > 0 && !hasResults;
      const showList = !isInitializing && hasResults;

      return (
        <ModalCenter
          type="default"
          ref={modalRef}
          title={translations.title}
          width="xl"
          data-testid={testId}
          onCloseModal={() => {
            setRemoteOpen(false);
            onOpenChange?.(false);
          }}
        >
          <div className="space-y-2 mt-2">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={translations.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  className="pl-10 pr-10"
                  data-testid={`${testId}-search`}
                  disabled={isInitializing}
                />
                {isSearching && !isInitializing && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      onSearchChange?.('');
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    data-testid={`${testId}-clear-search`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {error
                    ? null
                    : isInitializing || isLoading
                      ? translations.loadingProducts
                      : isSearching
                        ? t('productSelector.resultsCount', {
                            count: filteredProducts.length,
                            total: internalProducts.length,
                            s: filteredProducts.length !== 1 ? 's' : '',
                          })
                        : t('productSelector.productsAvailable', {
                            count: internalProducts.length,
                            s: internalProducts.length !== 1 ? 's' : '',
                          })}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {error && (
                <div role="alert" className="text-sm text-destructive">
                  <p>{t('dynamicCombobox.loadError')}</p>
                  <Button type="button" variant="outline" onClick={onRetry}>
                    {t('common.retry')}
                  </Button>
                </div>
              )}
              {showLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium mb-2">{translations.loadingProducts}</h3>
                  <p className="text-sm">{translations.preparingList}</p>
                </div>
              )}

              {showEmpty && (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium mb-2">{translations.noProductsAvailable}</h3>
                  <p className="text-sm">{translations.noProductsMessage}</p>
                </div>
              )}

              {showNoResults && (
                <div className="text-center py-12 text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium mb-2">
                    {isSearching ? translations.noResultsFound : translations.noProductsAvailable}
                  </h3>
                  <p className="text-sm">
                    {isSearching ? translations.noResultsMessage : translations.noProductsMessage}
                  </p>
                </div>
              )}

              {showList && (
                <div ref={virtualizedListRef} className="overflow-auto pr-2" style={{ height: '400px' }}>
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                      const product = filteredProducts[virtualItem.index];

                      return (
                        <div
                          key={virtualItem.key}
                          data-index={virtualItem.index}
                          ref={virtualizer.measureElement}
                          className="absolute top-0 left-0 w-full"
                          style={{
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          <ProductListItem
                            product={product}
                            onToggleSelect={handleToggleSelection}
                            onQuantityChange={handleQuantityChange}
                            maxQuantity={maxQuantity}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {hasMore && (
                <Button type="button" variant="outline" disabled={isLoading} onClick={onLoadMore}>
                  {t('search.loadMore')}
                </Button>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={handleCancel} data-testid={`${testId}-cancel`}>
                {translations.cancelButtonText}
              </Button>

              <Button
                type="button"
                variant="primary"
                onClick={handleConfirm}
                disabled={!hasSelectedProducts || isInitializing}
                data-testid={`${testId}-confirm`}
                className="min-w-[140px]"
              >
                {hasSelectedProducts
                  ? `${translations.confirmButtonText} (${getSelectedProducts().length})`
                  : translations.confirmButtonText}
              </Button>
            </div>
          </div>
        </ModalCenter>
      );
    },
  ),
);

ProductSelector.displayName = 'ProductSelector';

export default ProductSelector;
